import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, getToken } from "./api";
import { getInitData, isInTelegram, initTG, ensureTGScript, hasTelegramInitData, getStartParam } from "./telegram";

const AuthCtx = createContext(null);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function getUtmFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const utm = {};
    let found = false;
    ["source", "medium", "campaign", "term", "content"].forEach((k) => {
      const v = q.get(`utm_${k}`);
      if (v) {
        utm[k] = v.slice(0, 200);
        found = true;
      }
    });
    return found ? utm : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setAuthed(true);
      return data;
    } catch (e) {
      const status = e?.response?.status;
      setUser(null);
      setAuthed(false);
      if (status === 401 || status === 403) {
        setToken(null);
      }
      return null;
    }
  }, []);

  const loginTelegram = useCallback(async () => {
    ensureTGScript();
    initTG();

    let init = getInitData();
    for (let i = 0; i < 8 && !init; i += 1) {
      await sleep(150);
      init = getInitData();
    }

    if (!init) {
      const err = new Error("Telegram initData is missing");
      err.code = "TG_INIT_MISSING";
      throw err;
    }

    try {
      // start_param normally arrives via Telegram initData, but bot-chat web_app
      // button launches don't carry it there — bot.py bridges it as ?utm_source=
      // on the webapp URL instead (see bot.py's _url_with_utm), so fall back to
      // reading it from the URL for that path.
      let startParam = getStartParam();
      if (!startParam && typeof window !== "undefined") {
        startParam = new URLSearchParams(window.location.search).get("utm_source") || null;
      }
      const { data } = await api.post("/auth/telegram", { init_data: init, start_param: startParam });
      setToken(data.token);
      setUser(data.user);
      setAuthed(true);
      // Attach UTM metadata after login if present in URL
      const utm = getUtmFromUrl();
      if (utm) {
        api.patch("/profile", { utm }).catch(() => {});
      }
      // Silently refresh location after login
      try {
        if (typeof navigator !== "undefined" && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              api.post("/profile/location", {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
              }).catch(() => {});
            },
            () => {},
            { timeout: 5000 }
          );
        }
      } catch {}
      return data;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      const err = new Error(detail || "Telegram auth failed");
      err.code = status === 401 ? "TG_INIT_INVALID" : "TG_AUTH_FAILED";
      err.status = status;
      err.detail = detail;
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setAuthed(false);
  }, []);

  // Auto-init on mount
  useEffect(() => {
    ensureTGScript();

    // Safety timeout: force loading to false after 3s max
    const safetyTimer = setTimeout(() => setLoading(false), 3000);

    const boot = async () => {
      try {
        initTG();
      } catch {}
      if (getToken()) {
        const u = await refresh();
        if (u) {
          clearTimeout(safetyTimer);
          setLoading(false);
          // Returning user with a cached token skips loginTelegram() below, but
          // that's the only call that carries fresh initData (username/first_name
          // can change on Telegram's side) — resync it silently in the background
          // so a stale @handle doesn't linger until next logout.
          if (isInTelegram() && hasTelegramInitData()) {
            loginTelegram().catch(() => {});
          }
          return;
        }
      }
      // Try Telegram auto-login only if truly in Telegram AND have init data
      if (isInTelegram() && hasTelegramInitData()) {
        try {
          await loginTelegram();
          clearTimeout(safetyTimer);
          setLoading(false);
          return;
        } catch (e) {
          console.warn("TG auto-login failed:", e);
        }
      }
      clearTimeout(safetyTimer);
      setLoading(false);
    };
    // Wait a tick for TG script to load
    const timer = setTimeout(boot, 400);
    return () => {
      clearTimeout(timer);
      clearTimeout(safetyTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, authed, setAuthed, refresh, loginTelegram, logout, setToken }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
